import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Hr_API } from "./environment";
import { useAuth } from "../context/AuthContext";
import { FaEdit, FaTrash, FaPlus, FaFilter } from "react-icons/fa";
import Swal from "sweetalert2";
import Pagination from "../pages/Pagination";

// Component for managing HRs with add, edit, delete, multiple delete, and filter functionality
export default function Hrs() {
  const { userData } = useAuth();
  const [hrs, setHrs] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(5);
  const [filters, setFilters] = useState({
    hr_id: [],
    hr_name: [],
    hr_email: [],
    hr_mobile: [],
  });
  const [showFilterPopup, setShowFilterPopup] = useState(null);
  const [tempSelected, setTempSelected] = useState([]);
  const [searchInput, setSearchInput] = useState("");

  const columnDisplay = {
    hr_id: "HR ID",
    hr_name: "Name",
    hr_email: "Email",
    hr_mobile: "Mobile",
  };

  // Get distinct values for a column
  const getDistinctValues = (column) => {
    const values = hrs.map((item) => item[column]);
    const unique = [...new Set(values)];
    return unique.sort((a, b) =>
      column === "hr_id" ? a - b : a?.toString().localeCompare(b?.toString())
    );
  };

  // Apply filters to an item
  const applyFilters = (item) => {
    return (
      (filters.hr_id.length === 0 || filters.hr_id.includes(item.hr_id)) &&
      (filters.hr_name.length === 0 ||
        filters.hr_name.includes(item.hr_name)) &&
      (filters.hr_email.length === 0 ||
        filters.hr_email.includes(item.hr_email)) &&
      (filters.hr_mobile.length === 0 ||
        filters.hr_mobile.includes(item.hr_mobile))
    );
  };

  // Filter hrs
  const filteredHrs = hrs.filter(applyFilters);

  const totalPages = Math.ceil(filteredHrs.length / itemsPerPage);
  const paginatedHrs = filteredHrs.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const hasActiveFilters = Object.values(filters).some((arr) => arr.length > 0);

  // Fetch all hrs on component mount
  useEffect(() => {
    const fetchHrs = async () => {
      try {
        const response = await fetch(`${Hr_API}/getAll`, {
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });
        if (!response.ok) {
          throw new Error("Failed to fetch hrs");
        }
        const data = await response.json();
        setHrs(data);
      } catch (error) {
        console.error("Error fetching hrs:", error);
        Swal.fire("Error", "Failed to fetch hrs. Please try again.", "error");
      }
    };
    fetchHrs();
  }, [userData.access_token]);

  // Toggle row selection
  const toggleRowSelection = (id) => {
    setSelectedRows((prevSelected) =>
      prevSelected.includes(id)
        ? prevSelected.filter((rowId) => rowId !== id)
        : [...prevSelected, id]
    );
  };

  // Select all filtered hrs
  const handleSelectAll = () => {
    if (selectedRows.length === filteredHrs.length) {
      setSelectedRows([]);
    } else {
      setSelectedRows(filteredHrs.map((item) => item.hr_id));
    }
  };

  // Handle filter click for a column
  const handleFilterClick = (column) => {
    setShowFilterPopup(column);
    setTempSelected(filters[column] || []);
    setSearchInput("");
  };

  // Apply filter selections
  const applyFilter = () => {
    const distinct = getDistinctValues(showFilterPopup);
    const newSelected =
      tempSelected.length === distinct.length ? [] : tempSelected;
    setFilters((prevFilters) => ({
      ...prevFilters,
      [showFilterPopup]: newSelected,
    }));
    setCurrentPage(1);
    setShowFilterPopup(null);
  };

  // Delete multiple selected rows
  const deleteSelectedRows = async () => {
    if (selectedRows.length === 0) return;

    Swal.fire({
      text: `Do you want to delete ${selectedRows.length} selected hrs?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await Promise.all(
            selectedRows.map(async (id) => {
              const response = await fetch(`${Hr_API}/delete/${id}`, {
                method: "DELETE",
                headers: {
                  Authorization: `Bearer ${userData.access_token}`,
                  Accept: "application/json",
                },
              });

              if (!response.ok) {
                const errorText = await response.text();
                console.error("Server Error:", errorText);
                throw new Error(`Failed to delete hr ${id}`);
              }
            })
          );

          const updatedHrs = hrs.filter(
            (hr) => !selectedRows.includes(hr.hr_id)
          );
          setHrs(updatedHrs);
          setSelectedRows([]);

          Swal.fire("Success!", "Selected hrs deleted successfully", "success");
        } catch (error) {
          console.error("Error deleting hrs:", error);
          Swal.fire(
            "Error",
            "Failed to delete some hrs. Please try again.",
            "error"
          );
        }
      }
    });
  };

  // Function to add a new hr using SweetAlert for input
  const addHr = async () => {
    const { value } = await Swal.fire({
      title: "Create new HR",
      html:
        '<input id="swal-hr_name" class="swal2-input" placeholder="Enter name">' +
        '<input id="swal-hr_email" class="swal2-input" placeholder="Enter email">' +
        '<input id="swal-hr_mobile" class="swal2-input" placeholder="Enter mobile">',
      focusConfirm: false,
      showCancelButton: true,
      preConfirm: () => {
        const hr_name = document.getElementById("swal-hr_name").value;
        const hr_email = document.getElementById("swal-hr_email").value;
        const hr_mobile = document.getElementById("swal-hr_mobile").value;
        if (!hr_name || !hr_email || !hr_mobile) {
          Swal.showValidationMessage("All fields are required!");
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(hr_email)) {
          Swal.showValidationMessage("Please enter a valid email!");
          return false;
        }
        if (!/^\d{10}$/.test(hr_mobile)) {
          Swal.showValidationMessage(
            "Please enter a valid 10-digit mobile number!"
          );
          return false;
        }
        return { hr_name, hr_email, hr_mobile };
      },
    });

    if (value) {
      try {
        const response = await fetch(`${Hr_API}/create`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
          body: JSON.stringify({
            hr_name: value.hr_name,
            hr_email: value.hr_email,
            hr_mobile: value.hr_mobile,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Create Error Response:", errorText);
          throw new Error("Failed to add hr");
        }

        const newHr = await response.json();
        setHrs([...hrs, newHr]);
        Swal.fire("Success!", "HR added successfully.", "success");
      } catch (error) {
        console.error("Error adding hr:", error);
        Swal.fire("Error", "Failed to add hr. Please try again.", "error");
      }
    }
  };

  // Function to edit an existing hr using SweetAlert for input
  const editHr = async (hr) => {
    const { value } = await Swal.fire({
      title: "Edit HR",
      html:
        `<input id="swal-hr_name" class="swal2-input" placeholder="Enter name" value="${hr.hr_name}">` +
        `<input id="swal-hr_email" class="swal2-input" placeholder="Enter email" value="${hr.hr_email}">` +
        `<input id="swal-hr_mobile" class="swal2-input" placeholder="Enter mobile" value="${hr.hr_mobile}">`,
      focusConfirm: false,
      showCancelButton: true,
      preConfirm: () => {
        const hr_name = document.getElementById("swal-hr_name").value;
        const hr_email = document.getElementById("swal-hr_email").value;
        const hr_mobile = document.getElementById("swal-hr_mobile").value;
        if (!hr_name || !hr_email || !hr_mobile) {
          Swal.showValidationMessage("All fields are required!");
          return false;
        }
        if (!/\S+@\S+\.\S+/.test(hr_email)) {
          Swal.showValidationMessage("Please enter a valid email!");
          return false;
        }
        if (!/^\d{10}$/.test(hr_mobile)) {
          Swal.showValidationMessage(
            "Please enter a valid 10-digit mobile number!"
          );
          return false;
        }
        return { hr_name, hr_email, hr_mobile };
      },
    });

    if (value) {
      try {
        const response = await fetch(`${Hr_API}/update/${hr.hr_id}`, {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
          body: JSON.stringify({
            hr_name: value.hr_name,
            hr_email: value.hr_email,
            hr_mobile: value.hr_mobile,
          }),
        });

        if (!response.ok) {
          const errorText = await response.text();
          console.error("Update Error Response:", errorText);
          throw new Error("Failed to update hr");
        }

        const updatedHr = await response.json();
        setHrs(hrs.map((item) => (item.hr_id === hr.hr_id ? updatedHr : item)));
        Swal.fire("Success!", "HR updated successfully.", "success");
      } catch (error) {
        console.error("Error updating hr:", error);
        Swal.fire("Error", "Failed to update hr. Please try again.", "error");
      }
    }
  };

  // Function to delete a single hr with confirmation
  const deleteHr = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${Hr_API}/delete/${id}`, {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${userData.access_token}`,
            Accept: "application/json",
          },
        });

        if (!response.ok) {
          throw new Error("Failed to delete hr");
        }

        setHrs(hrs.filter((hr) => hr.hr_id !== id));
        Swal.fire("Deleted!", "HR has been deleted.", "success");
      } catch (error) {
        console.error("Error deleting hr:", error);
        Swal.fire("Error", "Failed to delete hr. Please try again.", "error");
      }
    }
  };

  // Render filter popup for a column
  const renderFilterPopup = (column) => {
    const displayName = columnDisplay[column];
    const distinctValues = getDistinctValues(column);
    const filteredValues = distinctValues.filter(
      (val) =>
        val != null &&
        val.toString().toLowerCase().includes(searchInput.toLowerCase())
    );
    const allVisibleSelected = filteredValues.every((val) =>
      tempSelected.includes(val)
    );

    const handleToggleVisible = (e) => {
      if (e.target.checked) {
        setTempSelected([...new Set([...tempSelected, ...filteredValues])]);
      } else {
        setTempSelected(
          tempSelected.filter((val) => !filteredValues.includes(val))
        );
      }
    };

    const clearSelections = () => {
      setFilters((prevFilters) => ({
        ...prevFilters,
        [column]: [],
      }));
      setTempSelected([]);
      setShowFilterPopup(null);
    };

    return (
      <div
        className="filter-popup"
        style={{
          position: "absolute",
          top: "100%",
          left: "0",
          zIndex: 100,
        }}
      >
        <div className="filter-header">
          <span className="column-name">{displayName}</span>
          <span className="clear-filter" onClick={clearSelections}>
            Clear
          </span>
        </div>
        <div className="filter-search-container">
          <input
            type="checkbox"
            checked={allVisibleSelected}
            onChange={handleToggleVisible}
          />
          <input
            className="filter-search"
            type="text"
            placeholder="Search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
          />
          {searchInput && (
            <span className="clear-search" onClick={() => setSearchInput("")}>
              ×
            </span>
          )}
        </div>
        <div className="suggestions-list">
          {filteredValues.length === 0 ? (
            <div>No results found</div>
          ) : (
            filteredValues.map((val) => (
              <label key={val?.toString() || "null"}>
                <input
                  type="checkbox"
                  checked={tempSelected.includes(val)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setTempSelected([...tempSelected, val]);
                    } else {
                      setTempSelected(tempSelected.filter((v) => v !== val));
                    }
                  }}
                />
                {val != null ? val.toString() : "N/A"}
              </label>
            ))
          )}
        </div>
        <div className="filter-buttons">
          <button onClick={() => setShowFilterPopup(null)}>Cancel</button>
          <button onClick={applyFilter}>Apply</button>
        </div>
      </div>
    );
  };

  return (
    <div className="relative">
      <Helmet>
        <title>HRs - SMTS Admin Panel</title>
      </Helmet>
      <div className="page-title">
        <h2>HRs</h2>
      </div>

      <div className="card">
        <table className="table">
          <thead>
            <tr>
              <th className="hide-column">
                <input
                  type="checkbox"
                  checked={
                    selectedRows.length === filteredHrs.length &&
                    filteredHrs.length > 0
                  }
                  onChange={handleSelectAll}
                />
              </th>
              <th style={{ position: "relative" }}>
                {hasActiveFilters && (
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === filteredHrs.length &&
                      filteredHrs.length > 0
                    }
                    onChange={handleSelectAll}
                  />
                )}
                HR ID
                <FaFilter
                  className={`filter-icon ${
                    filters.hr_id.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("hr_id")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "hr_id" && renderFilterPopup("hr_id")}
              </th>
              <th style={{ position: "relative" }}>
                Name
                <FaFilter
                  className={`filter-icon ${
                    filters.hr_name.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("hr_name")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "hr_name" && renderFilterPopup("hr_name")}
              </th>
              <th style={{ position: "relative" }}>
                Email
                <FaFilter
                  className={`filter-icon ${
                    filters.hr_email.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("hr_email")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "hr_email" &&
                  renderFilterPopup("hr_email")}
              </th>
              <th style={{ position: "relative" }}>
                Mobile
                <FaFilter
                  className={`filter-icon ${
                    filters.hr_mobile.length > 0 ? "active-filter" : ""
                  }`}
                  onClick={() => handleFilterClick("hr_mobile")}
                  style={{ marginLeft: "5px", cursor: "pointer" }}
                />
                {showFilterPopup === "hr_mobile" &&
                  renderFilterPopup("hr_mobile")}
              </th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {paginatedHrs.map((hr) => (
              <tr
                key={hr.hr_id}
                className={selectedRows.includes(hr.hr_id) ? "selected" : ""}
                onDoubleClick={() => toggleRowSelection(hr.hr_id)}
              >
                <td className="hide-column">
                  <input
                    type="checkbox"
                    checked={selectedRows.includes(hr.hr_id)}
                    onChange={() => toggleRowSelection(hr.hr_id)}
                  />
                </td>
                <td>{hr.hr_id}</td>
                <td>{hr.hr_name}</td>
                <td>{hr.hr_email}</td>
                <td>{hr.hr_mobile}</td>
                <td className="actions">
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => editHr(hr)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaEdit />
                  </button>
                  <button
                    className={`action-button ${
                      selectedRows.length > 1 ? "disabled-icon" : ""
                    }`}
                    onClick={() => deleteHr(hr.hr_id)}
                    disabled={selectedRows.length > 1}
                  >
                    <FaTrash />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        itemsPerPage={itemsPerPage}
        setCurrentPage={setCurrentPage}
        setItemsPerPage={(value) => {
          setCurrentPage(1);
          setItemsPerPage(value);
        }}
      />

      {selectedRows.length > 0 && (
        <button className="delete-selected" onClick={deleteSelectedRows}>
          <FaTrash className="icon" title="Delete Selected" /> (
          {selectedRows.length})
        </button>
      )}

      <button className="add-button" onClick={addHr}>
        <FaPlus />
      </button>
    </div>
  );
}
